'use client';
import { useEffect } from 'react';
/**
 * React Grab component for development
 *
 * Automatically loads react-grab in development mode only.
 * Can be explicitly enabled/disabled via the `enabled` prop.
 *
 * @example
 * ```tsx
 * // app/layout.tsx
 * import { ReactGrab } from '@sandstone/dev-tools';
 *
 * export default function RootLayout({ children }) {
 *   return (
 *     <html lang='en'>
 *       <body>
 *         {children}
 *         <ReactGrab />
 *       </body>
 *     </html>
 *   );
 * }
 * ```
 */
export function ReactGrab({ enabled, src = 'https://unpkg.com/react-grab/dist/index.global.js' }) {
    const shouldLoad = enabled ?? process.env.NODE_ENV === 'development';
    useEffect(() => {
        if (!shouldLoad) {
            // Remove script if it exists
            const existingScript = document.querySelector(`script[src="${src}"]`);
            if (existingScript) {
                existingScript.remove();
            }
            return;
        }
        // Check if script is already loaded
        if (document.querySelector(`script[src="${src}"]`)) {
            return;
        }
        const script = document.createElement('script');
        script.src = src;
        script.crossOrigin = 'anonymous';
        script.async = true;
        script.setAttribute('data-enabled', 'true');
        document.head.appendChild(script);
        return () => {
            // Cleanup: remove script on unmount
            const existingScript = document.querySelector(`script[src="${src}"]`);
            if (existingScript) {
                existingScript.remove();
            }
        };
    }, [shouldLoad, src]);
    return null;
}
